import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { Toggle } from "rsuite";
import { GlassCard, SectionRibbon, Button, colors } from "../../../../../ui";
import { SeverityAlert } from "../../../../../../appHelpers/appCommonSC";
import { DateTime } from "luxon";
import { UserContext } from "../../../_context/UserContext";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useAuth0 } from "@auth0/auth0-react";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";

export const AlertAllFC = ({ defaultConfigs, handleEnabled }) => {
  const { id } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const { userRoles } = useContext(UserContext);
  const [alertConfigs, setAlertConfigs] = useState([]);
  const [displayAlertConfigs, setDisplayAlertConfigs] = useState([]);
  const [loadingAlertConfig, setLoadingAlertConfig] = useState(true);
  const [getAlertConfigError, setGetAlertConfigError] = useState(true);
  const [hideAvante, setHideAvante] = useState(false);
  const [defaultIDs, setDefaultIDs] = useState([]);

  const getSystemAlertConfig = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "get",
        url: `/alert/configs/all-users/${id}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setAlertConfigs(response.data[0]);
      setDisplayAlertConfigs(response.data[0]);
    } catch (error) {
      setGetAlertConfigError(true);
      console.log(error);
    }
    setLoadingAlertConfig(false);
  };

  const handleHide = () => {
    if (!hideAvante) {
      setDisplayAlertConfigs(
        alertConfigs.filter(
          (config) => !config.user_id.toLowerCase().includes("avantehs.com"),
        ),
      );
    } else {
      setDisplayAlertConfigs(alertConfigs);
    }

    setHideAvante(!hideAvante);
  };

  useEffect(() => {
    getSystemAlertConfig();
  }, []);

  return (
    <ModernAlertContainer>
      <GlassCard animated visible={true}>
        <HeaderSection>
          <SectionTitle>All User Alerts</SectionTitle>
          {(userRoles.includes("superuser") ||
            userRoles.includes("alerter")) && (
            <ControlsGroup>
              <ControlLabel>Hide Avante Users</ControlLabel>
              <ModernCheckbox checked={hideAvante} onChange={handleHide}>
                <CheckboxInput
                  type="checkbox"
                  checked={hideAvante}
                  onChange={handleHide}
                />
                <CheckboxMark />
              </ModernCheckbox>
            </ControlsGroup>
          )}
        </HeaderSection>

        <AlertSectionsContainer>
          {defaultConfigs
            .filter(
              (config) =>
                config.user_id === "default" &&
                !config.pg_table.toLowerCase().includes("log") &&
                !config.pg_table.toLowerCase().includes("hhm"),
            )
            .map((def, defIndex) => (
              <AlertSection key={def.id}>
                <DefaultAlertHeader>
                  <DefaultTag>DEFAULT</DefaultTag>
                  <DefaultAlertText>
                    {def.field_name_alias} {def?.operator?.replace(/_/g, " ")}{" "}
                    {def?.threshold} {def?.threshold_units}
                  </DefaultAlertText>
                </DefaultAlertHeader>

                <AlertTable>
                  <TableHeader>
                    <HeaderCell>USER</HeaderCell>
                    <HeaderCell>FIELD NAME</HeaderCell>
                    <HeaderCell>OPERATOR</HeaderCell>
                    <HeaderCell>THRESHOLD</HeaderCell>
                    <HeaderCell>SEVERITY</HeaderCell>
                    <HeaderCell>FREQUENCY</HeaderCell>
                    <HeaderCell>ENABLED</HeaderCell>
                    {(userRoles.includes("superuser") ||
                      userRoles.includes("alerter")) && (
                      <>
                        <HeaderCell>UPDATED BY</HeaderCell>
                        <HeaderCell>UPDATED</HeaderCell>
                      </>
                    )}
                  </TableHeader>

                  {displayAlertConfigs
                    .filter((conf) => conf.default_id === def.id)
                    .map((config) => (
                      <TableRow key={config.id}>
                        <TableCell>
                          <UserEmail>{config?.user_id}</UserEmail>
                        </TableCell>
                        <TableCell>{config?.field_name_alias}</TableCell>
                        <TableCell>
                          {config?.operator?.replace(/_/g, " ")}
                        </TableCell>
                        <TableCell>
                          {config?.threshold} {config?.threshold_units}
                        </TableCell>
                        <TableCell>
                          <SeverityAlert severity={config?.severity}>
                            {config?.severity}
                          </SeverityAlert>
                        </TableCell>
                        <TableCell>
                          <FrequencyText>
                            {config.notify_frequency
                              ? config.notify_frequency
                              : defaultConfigs.find(
                                  (def) => def.id === config.default_id,
                                )?.notify_frequency}
                          </FrequencyText>
                          hours
                        </TableCell>
                        <TableCell>
                          {(userRoles.includes("superuser") ||
                            userRoles.includes("alerter") ||
                            userRoles.includes("admin")) && (
                            <Toggle
                              defaultChecked={config.enabled}
                              onClick={() => handleEnabled(config.id)}
                            />
                          )}
                        </TableCell>
                        {(userRoles.includes("superuser") ||
                          userRoles.includes("alerter")) && (
                          <>
                            <TableCell>{config?.last_updated_by}</TableCell>
                            <TableCell>
                              {DateTime.fromISO(config?.last_update).toFormat(
                                "MMM dd, yyyy HH:mm",
                              )}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                </AlertTable>
              </AlertSection>
            ))}
        </AlertSectionsContainer>
      </GlassCard>
    </ModernAlertContainer>
  );
};

// Modern Styled Components with Glass Morphism
const ModernAlertContainer = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  margin-bottom: 16px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.9),
    rgba(255, 255, 255, 0.7)
  );
  border: 2px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.neutral[800]};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ControlsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ControlLabel = styled.label`
  color: ${colors.neutral[700]};
  font-size: 13px;
  font-weight: 500;
`;

const ModernCheckbox = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;

const CheckboxInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const CheckboxMark = styled.div`
  position: relative;
  width: 20px;
  height: 20px;
  background: white;
  border: 2px solid ${colors.neutral[400]};
  border-radius: 4px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;

  ${CheckboxInput}:checked + & {
    background: ${colors.primary.main};
    border-color: ${colors.primary.main};
  }

  ${CheckboxInput}:checked + &::after {
    content: "";
    position: absolute;
    left: 5px;
    top: 1px;
    width: 6px;
    height: 11px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  &:hover {
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 2px rgba(44, 90, 160, 0.1);
  }
`;

const AlertSectionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const AlertSection = styled.div`
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.9),
    rgba(255, 255, 255, 0.7)
  );
  border: 2px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 12px 16px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.95),
      rgba(255, 255, 255, 0.85)
    );
    border-color: ${colors.primary.main};
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
`;

const DefaultAlertHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: linear-gradient(
    135deg,
    ${colors.primary.main}15,
    ${colors.primary.main}05
  );
  border: 1px solid ${colors.primary.main}30;
  border-radius: 8px;
`;

const DefaultTag = styled.span`
  background: ${colors.primary.main};
  color: white;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DefaultAlertText = styled.span`
  color: ${colors.neutral[800]};
  font-size: 14px;
  font-weight: 500;
`;

const AlertTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-x: auto;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr 0.8fr 0.8fr 0.8fr 1.5fr 1fr;
  gap: 10px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  margin-bottom: 4px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);

  @media screen and (max-width: 1024px) {
    grid-template-columns: 1.5fr 1fr 1fr 1fr 0.8fr 0.8fr 0.8fr;
  }
`;

const HeaderCell = styled.div`
  color: ${colors.neutral[600]};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr 0.8fr 0.8fr 0.8fr 1.5fr 1fr;
  gap: 10px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  align-items: center;

  &:hover {
    background: rgba(255, 255, 255, 0.95);
    border-color: ${colors.primary.main};
    transform: translateY(-1px);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  }

  @media screen and (max-width: 1024px) {
    grid-template-columns: 1.5fr 1fr 1fr 1fr 0.8fr 0.8fr 0.8fr;
  }
`;

const TableCell = styled.div`
  color: ${colors.neutral[800]};
  font-size: 13px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
`;

const UserEmail = styled.div`
  background: ${colors.glass.surface};
  border: 1px solid ${colors.glass.border};
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FrequencyText = styled.span`
  font-weight: 600;
  color: ${colors.primary.main};
  margin-right: 4px;
`;
